/* Boombox audio engine — every station is synthesized live with the WebAudio API.
   No samples, no files, no streams: the music is generated in the overlay, so it
   can never trip a copyright claim. Shared by the phone Boombox app and the
   in-car radio HUD so a station sounds identical everywhere. */

import { useCallback, useEffect, useRef, useState } from "react";

export const STATIONS = [
  { id: "nightride", name: "Nightride FM", freq: "88.9", genre: "synthwave / outrun", color: "#f472b6",
    tracks: ["Chrome Rivers — Vaslov", "4AM Overpass — Meridian", "Neon Harvest — Kaito Prime", "Static Bloom — The Dials"] },
  { id: "dockside", name: "Dockside Talk", freq: "91.4", genre: "talk / call-ins", color: "#38bdf8",
    tracks: ["The Harbour Report", "Mayor's Office Hour", "Corner Economics w/ Finchy", "Night Shift Confessions"] },
  { id: "gold", name: "Bornwood Gold", freq: "96.2", genre: "classic rock", color: "#fbbf24",
    tracks: ["Long Haul — The Semis", "Wheatbelt Sky — Aurora Vale", "Two Lane Blacktop — Crows", "Milltown — Static Sons"] },
  { id: "static", name: "The Static", freq: "101.7", genre: "pirate / late noise", color: "#4ade80",
    tracks: ["[signal lost]", "Numbers Station — ???:??", "Tape Loop 14 — Unknown", "Found Frequency — Empty Vessels"] },
];

let _ctx = null;
function audioCtx() {
  if (!_ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    _ctx = new C();
  }
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);
const pick = (a) => a[Math.floor(Math.random() * a.length)];

function makeNoise(ctx, secs = 1.5) {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * secs), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/* one scheduled oscillator note through an optional filter; dest may be an array (dry + echo bus) */
function note(ctx, dest, { f, t, d, type = "sine", peak = 0.1, a = 0.01, slideTo = 0, filterF = 0, filterType = "lowpass", q = 1 }) {
  const o = ctx.createOscillator(); o.type = type;
  o.frequency.setValueAtTime(Math.max(f, 20), t);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(slideTo, 20), t + d);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t + d);
  let head = o;
  if (filterF) {
    const fl = ctx.createBiquadFilter(); fl.type = filterType; fl.frequency.value = filterF; fl.Q.value = q;
    o.connect(fl); head = fl;
  }
  head.connect(g);
  (Array.isArray(dest) ? dest : [dest]).forEach((dd) => g.connect(dd));
  o.start(t); o.stop(t + d + 0.05);
}

function noiseHit(ctx, dest, noise, { t, d, peak = 0.05, filterF = 1200, filterType = "bandpass", q = 1 }) {
  const src = ctx.createBufferSource(); src.buffer = noise; src.loop = true;
  const fl = ctx.createBiquadFilter(); fl.type = filterType; fl.frequency.value = filterF; fl.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + d);
  src.connect(fl); fl.connect(g);
  (Array.isArray(dest) ? dest : [dest]).forEach((dd) => g.connect(dd));
  src.start(t); src.stop(t + d + 0.05);
}

/* ---------------- station generators — each schedules one block and returns the next t ---------------- */

// Nightride FM — synthwave: driving 8th bass, random-walk 16th arp into echo, slow pad
const NR_CHORDS = [
  { root: 33, tones: [57, 60, 64] }, // Am
  { root: 33, tones: [57, 60, 64] },
  { root: 29, tones: [53, 57, 60] }, // F
  { root: 31, tones: [55, 59, 62] }, // G
];
let nrBar = 0, nrArp = 64;
function nightride(ctx, dest, echo, noise, t) {
  const beat = 60 / 104, bar = beat * 4;
  const ch = NR_CHORDS[nrBar % NR_CHORDS.length];
  ch.tones.forEach((m, i) =>
    note(ctx, dest, { f: mtof(m) * (1 + i * 0.0016), t, d: bar * 1.05, type: "sawtooth", peak: 0.015, a: 0.5, filterF: 850 }));
  for (let i = 0; i < 8; i++)
    note(ctx, dest, { f: mtof(ch.root), t: t + (i * beat) / 2, d: beat * 0.4, type: "sawtooth", peak: 0.1, filterF: 260, a: 0.008 });
  for (let i = 0; i < 4; i++) {
    note(ctx, dest, { f: 120, slideTo: 44, t: t + i * beat, d: 0.13, peak: 0.15 });
    noiseHit(ctx, dest, noise, { t: t + i * beat + beat / 2, d: 0.035, peak: 0.012, filterF: 7000, filterType: "highpass" });
  }
  for (let i = 0; i < 16; i++) {
    nrArp += pick([-5, -3, -2, 0, 2, 3, 5]);
    while (nrArp > 76) nrArp -= 12;
    while (nrArp < 60) nrArp += 12;
    note(ctx, [dest, echo], { f: mtof(nrArp), t: t + (i * beat) / 4, d: 0.11, type: "square", peak: 0.028, filterF: 2400, a: 0.004 });
  }
  nrBar++;
  return t + bar;
}

// Bornwood Gold — 12-bar blues shuffle: triangle bass, thump/snare, pentatonic lead licks
const GD_SEQ = [40, 40, 40, 40, 45, 45, 40, 40, 47, 45, 40, 47]; // E / A / B roots
const GD_PENT = [52, 55, 57, 59, 61, 64, 67];
let gdBar = 0;
function gold(ctx, dest, echo, noise, t) {
  const beat = 60 / 96, bar = beat * 4;
  const root = GD_SEQ[gdBar % GD_SEQ.length];
  [root, root + 7, root, root + 7].forEach((m, i) =>
    note(ctx, dest, { f: mtof(m - 12), t: t + i * beat, d: beat * 0.8, type: "triangle", peak: 0.12, a: 0.01 }));
  [0, 2].forEach((b) => note(ctx, dest, { f: 110, slideTo: 42, t: t + b * beat, d: 0.13, peak: 0.14 }));
  [1, 3].forEach((b) => noiseHit(ctx, dest, noise, { t: t + b * beat, d: 0.09, peak: 0.028, filterF: 1800, filterType: "bandpass", q: 0.8 }));
  if (Math.random() < 0.75) {
    const n = 2 + Math.floor(Math.random() * 3);
    let tt = t + pick([0, beat / 2, beat]);
    let last = pick(GD_PENT);
    for (let i = 0; i < n && tt < t + bar - 0.06; i++) {
      const m = Math.random() < 0.5 ? last : pick(GD_PENT);
      const d = Math.min(pick([beat / 2, beat / 2, beat]), t + bar - tt - 0.03);
      note(ctx, [dest, echo], { f: mtof(m), t: tt, d, type: "sawtooth", peak: 0.042, filterF: 1500, a: 0.02, slideTo: Math.random() < 0.25 ? mtof(m + 2) : 0 });
      tt += d; last = m;
    }
  }
  gdBar++;
  return t + bar;
}

// Dockside Talk — syllabic "voice" murmur over a room bed, station ident every 5th block
let dkBlock = 0;
function dockside(ctx, dest, echo, noise, t) {
  const BLK = 4;
  noiseHit(ctx, dest, noise, { t, d: BLK, peak: 0.012, filterF: 260, filterType: "lowpass" });
  if (dkBlock % 5 === 4) { // ident bumper
    [659, 523, 659, 784].forEach((f, i) =>
      note(ctx, [dest, echo], { f, t: t + 0.6 + i * 0.32, d: 0.3, type: "triangle", peak: 0.07 }));
    noiseHit(ctx, dest, noise, { t: t + 2.6, d: 0.5, peak: 0.02, filterF: 5000, filterType: "highpass" });
  } else {
    let tt = t + 0.1;
    while (tt < t + BLK - 0.25) {
      const words = 3 + Math.floor(Math.random() * 6);
      for (let i = 0; i < words && tt < t + BLK - 0.2; i++) {
        const d = 0.07 + Math.random() * 0.13;
        note(ctx, dest, { f: 95 + Math.random() * 50, slideTo: 90 + Math.random() * 60, t: tt, d, type: "sawtooth", peak: 0.032, filterF: 380 + Math.random() * 420, filterType: "bandpass", q: 2.5, a: 0.02 });
        tt += d + 0.03 + Math.random() * 0.05;
      }
      tt += 0.35 + Math.random() * 0.6; // breath between sentences
    }
  }
  dkBlock++;
  return t + BLK;
}

// The Static — wandering band hiss, numbers-station beep groups, tape warble, dropouts
function staticSt(ctx, dest, echo, noise, t) {
  const BLK = 4;
  const drop = Math.random() < 0.22;
  noiseHit(ctx, dest, noise, { t, d: drop ? BLK * 0.35 : BLK, peak: 0.026, filterF: 500 + Math.random() * 2800, filterType: "bandpass", q: 1.4 });
  if (Math.random() < 0.5) {
    const f = pick([720, 880, 990, 1100]);
    const n = 3 + Math.floor(Math.random() * 5);
    for (let i = 0; i < n; i++) note(ctx, [dest, echo], { f, t: t + 0.4 + i * 0.3, d: 0.1, peak: 0.042 });
  }
  if (Math.random() < 0.3)
    note(ctx, dest, { f: 220 + Math.random() * 220, slideTo: 180 + Math.random() * 260, t: t + 1 + Math.random() * 2, d: 0.8, peak: 0.018, a: 0.2 });
  return t + BLK;
}

const BUILDERS = { nightride, dockside, gold, static: staticSt };

/* ---------------- engine handle ---------------- */

export function startStation(id, volume = 0.8) {
  const ctx = audioCtx();
  if (!ctx) return { stop() {}, setVolume() {} };
  const master = ctx.createGain(); master.gain.value = Math.max(volume, 0.0001); master.connect(ctx.destination);
  const delay = ctx.createDelay(1); delay.delayTime.value = id === "nightride" ? 0.34 : 0.26;
  const fb = ctx.createGain(); fb.gain.value = 0.3;
  delay.connect(fb); fb.connect(delay); delay.connect(master);
  const noise = makeNoise(ctx);
  const sched = BUILDERS[id] || BUILDERS.nightride;
  let nextT = ctx.currentTime + 0.08;
  const timer = setInterval(() => {
    if (ctx.state !== "running") return;
    while (nextT < ctx.currentTime + 0.25) nextT = sched(ctx, master, delay, noise, nextT);
  }, 40);
  return {
    stop() {
      clearInterval(timer);
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.05);
      setTimeout(() => { try { master.disconnect(); delay.disconnect(); } catch { /* already gone */ } }, 400);
    },
    setVolume(v) { master.gain.setTargetAtTime(Math.max(v, 0.0001), ctx.currentTime, 0.03); },
  };
}

/* React binding — one station at a time, engine dies with the component */
export function useStationPlayer() {
  const ref = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [volume, setVol] = useState(0.8);

  const stop = useCallback(() => { ref.current?.stop(); ref.current = null; setPlayingId(null); }, []);
  const play = useCallback((id, vol) => {
    ref.current?.stop();
    ref.current = startStation(id, vol ?? volume);
    setPlayingId(id);
  }, [volume]);
  const setVolume = useCallback((v) => { setVol(v); ref.current?.setVolume(v); }, []);

  useEffect(() => () => { ref.current?.stop(); ref.current = null; }, []);
  return { playingId, play, stop, setVolume, volume };
}
